<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WhatsappCatalogsAPI from 'dashboard/api/whatsappCatalogs';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ProductFormDialog from './ProductFormDialog.vue';
import {
  filterProducts,
  formatCents,
  isHttpsUrl,
  optionKey,
} from './catalogUtils';

// Produtos vêm diretamente da Meta; este painel só é montado depois que o
// usuário clica em "Ver produtos" em um catálogo.
const props = defineProps({
  catalog: { type: Object, required: true },
});
const emit = defineEmits(['catalogUpdated', 'issues', 'close']);

const { t } = useI18n();
const products = ref([]);
const after = ref(null);
const search = ref('');
const isLoading = ref(false);
const isLoadingMore = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const pendingDelete = ref(null);
const requestToken = ref(0);
const formRef = ref(null);
const deleteRef = ref(null);

const filteredProducts = computed(() =>
  filterProducts(products.value, search.value)
);

const errorText = error =>
  error?.response?.data?.error || t('WHATSAPP_CATALOGS.ACTION_ERROR');

const reportError = (titleKey, error) =>
  emit('issues', {
    title: t(titleKey),
    issues: error?.response?.data?.errors?.length
      ? error.response.data.errors
      : [errorText(error)],
  });

const loadProducts = async ({ more = false } = {}) => {
  requestToken.value += 1;
  const token = requestToken.value;
  if (more) isLoadingMore.value = true;
  else isLoading.value = true;
  try {
    const { data } = await WhatsappCatalogsAPI.products(
      props.catalog.id,
      more ? after.value : ''
    );
    if (token !== requestToken.value) return;
    products.value = more
      ? [...products.value, ...(data.payload || [])]
      : data.payload || [];
    after.value = data.after || null;
  } catch (error) {
    if (token === requestToken.value) {
      reportError('WHATSAPP_CATALOGS.ISSUES.LOAD_ERROR_TITLE', error);
    }
  } finally {
    if (token === requestToken.value) {
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  }
};

const openProduct = product => formRef.value?.open(product);

const saveProduct = async ({ id, payload }) => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    if (id) {
      const { data } = await WhatsappCatalogsAPI.updateProduct(
        props.catalog.id,
        id,
        payload
      );
      const index = products.value.findIndex(item => item.id === id);
      if (index >= 0) products.value.splice(index, 1, data.product);
      useAlert(t('WHATSAPP_CATALOGS.PRODUCT_FORM.UPDATE_SUCCESS'));
    } else {
      const { data } = await WhatsappCatalogsAPI.createProduct(
        props.catalog.id,
        payload
      );
      products.value.unshift(data.product);
      emit('catalogUpdated', data.catalog);
      useAlert(t('WHATSAPP_CATALOGS.PRODUCT_FORM.CREATE_SUCCESS'));
    }
    formRef.value?.close();
  } catch (error) {
    reportError('WHATSAPP_CATALOGS.ISSUES.PRODUCT_ERROR_TITLE', error);
  } finally {
    isSaving.value = false;
  }
};

const askDelete = product => {
  pendingDelete.value = product;
  deleteRef.value?.open();
};

const deleteProduct = async () => {
  const product = pendingDelete.value;
  if (!product || isDeleting.value) return;
  isDeleting.value = true;
  try {
    const { data } = await WhatsappCatalogsAPI.deleteProduct(
      props.catalog.id,
      product
    );
    products.value = products.value.filter(item => item.id !== product.id);
    emit('catalogUpdated', data.catalog);
    useAlert(t('WHATSAPP_CATALOGS.DELETE.SUCCESS'));
    deleteRef.value?.close();
    pendingDelete.value = null;
  } catch (error) {
    deleteRef.value?.close();
    reportError('WHATSAPP_CATALOGS.ISSUES.ACTION_ERROR_TITLE', error);
  } finally {
    isDeleting.value = false;
  }
};

const priceLabel = product =>
  Number.isInteger(product.price_cents)
    ? formatCents(product.price_cents)
    : product.price_label || '';

onMounted(() => loadProducts());

defineExpose({ loadProducts });
</script>

<template>
  <section
    class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
    :aria-label="t('WHATSAPP_CATALOGS.PRODUCTS.TITLE', { name: catalog.name })"
  >
    <header
      class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
    >
      <div class="min-w-0">
        <h3 class="mb-1 truncate text-base font-semibold text-n-slate-12">
          {{ t('WHATSAPP_CATALOGS.PRODUCTS.TITLE', { name: catalog.name }) }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('WHATSAPP_CATALOGS.PRODUCTS.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <NextButton
          type="button"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-hugeicons-refresh"
          :label="t('WHATSAPP_CATALOGS.PRODUCTS.RELOAD')"
          :is-loading="isLoading"
          @click="loadProducts()"
        />
        <NextButton
          type="button"
          size="sm"
          icon="i-hugeicons-add-01"
          :label="t('WHATSAPP_CATALOGS.PRODUCTS.NEW')"
          @click="openProduct(null)"
        />
        <NextButton
          type="button"
          size="sm"
          variant="ghost"
          color="slate"
          icon="i-hugeicons-cancel-01"
          :aria-label="t('WHATSAPP_CATALOGS.PRODUCTS.CLOSE')"
          :title="t('WHATSAPP_CATALOGS.PRODUCTS.CLOSE')"
          @click="emit('close')"
        />
      </div>
    </header>

    <div class="relative max-w-md">
      <span
        class="i-hugeicons-search-01 pointer-events-none absolute top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-10 ltr:left-3 rtl:right-3"
        aria-hidden="true"
      />
      <Input
        v-model="search"
        type="search"
        :placeholder="t('WHATSAPP_CATALOGS.PRODUCTS.SEARCH')"
        :aria-label="t('WHATSAPP_CATALOGS.PRODUCTS.SEARCH_LABEL')"
        custom-input-class="ltr:!pl-9 rtl:!pr-9"
      />
    </div>

    <div
      v-if="isLoading"
      class="flex min-h-32 items-center justify-center text-sm text-n-slate-11"
      role="status"
    >
      {{ t('WHATSAPP_CATALOGS.LOADING') }}
    </div>
    <p
      v-else-if="!products.length"
      class="mb-0 rounded-lg bg-n-alpha-1 p-4 text-center text-sm text-n-slate-11"
    >
      {{ t('WHATSAPP_CATALOGS.PRODUCTS.EMPTY') }}
    </p>
    <p
      v-else-if="!filteredProducts.length"
      class="mb-0 rounded-lg bg-n-alpha-1 p-4 text-center text-sm text-n-slate-11"
    >
      {{ t('WHATSAPP_CATALOGS.PRODUCTS.NO_RESULTS') }}
    </p>
    <ul
      v-else
      class="m-0 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-2 xl:grid-cols-3"
    >
      <li
        v-for="product in filteredProducts"
        :key="product.id"
        class="flex min-w-0 gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
      >
        <img
          v-if="isHttpsUrl(product.image_url)"
          :src="product.image_url"
          :alt="product.name"
          loading="lazy"
          class="size-16 shrink-0 rounded-md border border-n-weak bg-n-solid-1 object-contain"
        />
        <span
          v-else
          class="grid size-16 shrink-0 place-items-center rounded-md border border-n-weak bg-n-solid-1 text-n-slate-10"
          :title="t('WHATSAPP_CATALOGS.PRODUCTS.NO_IMAGE')"
        >
          <span class="i-hugeicons-image-01 size-5" aria-hidden="true" />
        </span>
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <p
            class="mb-0 truncate text-sm font-semibold text-n-slate-12"
            :title="product.name"
          >
            {{ product.name }}
          </p>
          <p class="mb-0 truncate text-xs text-n-slate-11">
            {{
              t('WHATSAPP_CATALOGS.PRODUCTS.SKU', { sku: product.retailer_id })
            }}
          </p>
          <div class="flex flex-wrap items-center gap-1.5 text-xs">
            <span class="font-medium text-n-slate-12">
              {{ priceLabel(product) }}
            </span>
            <span
              v-if="product.availability"
              class="rounded-md px-1.5 py-0.5"
              :class="
                product.availability === 'in stock'
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
            >
              {{
                t(
                  `WHATSAPP_CATALOGS.PRODUCTS.AVAILABILITY.${optionKey(product.availability)}`
                )
              }}
            </span>
          </div>
          <div class="mt-auto flex flex-wrap gap-1 pt-1">
            <NextButton
              type="button"
              size="xs"
              variant="ghost"
              color="slate"
              icon="i-hugeicons-pencil-edit-02"
              :label="t('WHATSAPP_CATALOGS.PRODUCTS.EDIT')"
              @click="openProduct(product)"
            />
            <NextButton
              type="button"
              size="xs"
              variant="ghost"
              color="ruby"
              icon="i-hugeicons-delete-02"
              :aria-label="t('WHATSAPP_CATALOGS.PRODUCTS.DELETE')"
              :title="t('WHATSAPP_CATALOGS.PRODUCTS.DELETE')"
              @click="askDelete(product)"
            />
          </div>
        </div>
      </li>
    </ul>

    <div v-if="after && !isLoading" class="flex justify-center">
      <NextButton
        type="button"
        size="sm"
        variant="faded"
        color="slate"
        :label="t('WHATSAPP_CATALOGS.PRODUCTS.LOAD_MORE')"
        :is-loading="isLoadingMore"
        @click="loadProducts({ more: true })"
      />
    </div>

    <ProductFormDialog
      ref="formRef"
      :is-saving="isSaving"
      @save="saveProduct"
      @error="
        ({ title, message }) => emit('issues', { title, issues: [message] })
      "
    />
    <Dialog
      ref="deleteRef"
      type="alert"
      :title="t('WHATSAPP_CATALOGS.DELETE.TITLE')"
      :description="
        t('WHATSAPP_CATALOGS.DELETE.DESCRIPTION', {
          name: pendingDelete?.name || '',
        })
      "
      :confirm-button-label="t('WHATSAPP_CATALOGS.DELETE.CONFIRM')"
      :is-loading="isDeleting"
      @confirm="deleteProduct"
    />
  </section>
</template>
